import "@arco-design/web-react/es/Card/style";
import _Card from "@arco-design/web-react/es/Card";
import "@arco-design/web-react/es/PageHeader/style";
import _PageHeader from "@arco-design/web-react/es/PageHeader";
import "@arco-design/web-react/es/Spin/style";
import _Spin from "@arco-design/web-react/es/Spin";
import "@arco-design/web-react/es/Empty/style";
import _Empty from "@arco-design/web-react/es/Empty";
import "@arco-design/web-react/es/Alert/style";
import _Alert from "@arco-design/web-react/es/Alert";
import "@arco-design/web-react/es/Space/style";
import _Space from "@arco-design/web-react/es/Space";
import "@arco-design/web-react/es/Popconfirm/style";
import _Popconfirm from "@arco-design/web-react/es/Popconfirm";
import _IconDownload from "@arco-design/web-react/icon/react-icon/IconDownload";
import _IconEye from "@arco-design/web-react/icon/react-icon/IconEye";
import "@arco-design/web-react/es/Button/style";
import _Button from "@arco-design/web-react/es/Button";
import "@arco-design/web-react/es/Switch/style";
import _Switch from "@arco-design/web-react/es/Switch";
import "@arco-design/web-react/es/Input/style";
import _Input from "@arco-design/web-react/es/Input";
import "@arco-design/web-react/es/Message/style";
import _Message from "@arco-design/web-react/es/Message";
import "@arco-design/web-react/es/Form/style";
import _Form from "@arco-design/web-react/es/Form";
import "@arco-design/web-react/es/Grid/style";
import _Grid from "@arco-design/web-react/es/Grid";
import "@arco-design/web-react/es/Steps/style";
import _Steps from "@arco-design/web-react/es/Steps";
import "@arco-design/web-react/es/Typography/style";
import _Typography from "@arco-design/web-react/es/Typography";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import Navbar from '@/components/NavBar';
import { UploadExcel } from '@/components/UploadExcel';
import request from '@/utils/request';
import useLocale from '@/utils/useLocale';
import DefaultPreview from './default';
import locale from './locale';
import styles from './style/index.module.less';
const {
  Text
} = _Typography;
const Step = _Steps.Step;
const {
  Row,
  Col
} = _Grid;

/**
 * 步骤状态（后端字符串枚举）：pending 初始 / processing 后台处理中 /
 * completed 完成 / failed 失败 / skipped 用户主动跳过。
 * 以下列出需显式判定的四态，pending 为默认。
 */
const STATUS_PROCESSING = 'processing';
const STATUS_COMPLETED = 'completed';
const STATUS_FAILED = 'failed';
const STATUS_SKIPPED = 'skipped';

/** 地址步骤为前端表单，其余步骤支持模板上传；confirm_url 非空则可应用系统默认值 */
const ADDRESS_STEP = 'address';

/** OSS 直传业务类型：init 专属 key_prefix */
const OSS_BIZ_TYPE = 'init';

/** 后端异步处理轮询：每 2s 一次，最多 60 次（约 2 分钟） */
const POLL_INTERVAL = 2000;
const POLL_MAX_TIMES = 60;

/** 后端返回的 template_url / preview_url / confirm_url 含网关前缀，转成 request 的相对路径以复用鉴权头 */
const API_PREFIX = process.env.REACT_APP_API_BASE_URL || '/api';
function toRequestPath(url) {
  return url.startsWith(API_PREFIX) ? url.slice(API_PREFIX.length) : url;
}

/** 对应后端 /init/progress 返回的步骤项 */

/** completed 与 skipped 均视为该步骤已了结，进度推进到下一步 */
function isSettled(step) {
  return step.status === STATUS_COMPLETED || step.status === STATUS_SKIPPED;
}
function InitPage() {
  const t = useLocale(locale);
  const [steps, setSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [templateLoading, setTemplateLoading] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [previewStep, setPreviewStep] = useState('');
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewValue, setPreviewValue] = useState();
  /** 后台异步处理中（轮询）的 step_id，非空表示该步骤正在解析校验 */
  const [processingStep, setProcessingStep] = useState('');
  const [addressForm] = _Form.useForm();
  const pollTimerRef = useRef(null);
  const pollCountRef = useRef(0);
  /** 当前正在轮询的 step_id，防止 fetchProgress 与上传回调重复启动轮询 */
  const pollingStepRef = useRef('');
  useEffect(() => {
    fetchProgress();
    return () => {
      if (pollTimerRef.current) clearTimeout(pollTimerRef.current);
    };
  }, []);
  function fetchProgress() {
    setLoading(true);
    request.get('/v1/init/progress').then(res => {
      const {
        code,
        message,
        data
      } = res.data;
      if (code !== 200) {
        _Message.error(message);
        return;
      }
      const list = Array.isArray(data) ? data : data?.list || [];
      setSteps(list);
      // 刷新页面后若存在后台处理中的步骤，自动恢复轮询，避免 processing 卡死
      const processing = list.find(s => s.status === STATUS_PROCESSING);
      if (processing && pollingStepRef.current !== processing.step_id) {
        startPolling(processing.step_id);
      }
    }).finally(() => setLoading(false));
  }

  /** 当前步骤 = 第一个未了结（非 completed/skipped）的步骤，顺序即依赖链 */
  const current = useMemo(() => steps.find(s => !isSettled(s)), [steps]);
  const currentIdx = current ? steps.indexOf(current) : steps.length;
  const doneCount = steps.filter(s => isSettled(s)).length;
  const allDone = steps.length > 0 && doneCount === steps.length;

  /** confirm_url 非空表示该步骤可应用系统默认值 */
  function canConfirm(step) {
    return !!step.confirm_url;
  }

  /** preview_url 非空表示该步骤可预览系统默认值 */
  function canPreview(step) {
    return !!step.preview_url;
  }

  /** skip_url 非空表示该步骤可跳过（material/bom/supplier） */
  function canSkip(step) {
    return !!step.skip_url;
  }

  /** 使用系统默认值：POST confirm_url（body 带 step_id），后端直接将该步骤置为已完成 */
  function onUseDefault(step) {
    setSubmitting(true);
    request.post(toRequestPath(step.confirm_url), {
      step_id: step.step_id
    }).then(res => {
      const {
        code,
        message
      } = res.data;
      if (code === 200) {
        _Message.success(message);
      } else {
        _Message.error(message);
      }
      fetchProgress();
    }).finally(() => setSubmitting(false));
  }

  /** 跳过该步骤：POST skip_url（body 带 step_id），后端置为 skipped 并推进进度 */
  function onSkip(step) {
    setSubmitting(true);
    request.post(toRequestPath(step.skip_url), {
      step_id: step.step_id
    }).then(res => {
      const {
        code,
        message
      } = res.data;
      if (code === 200) {
        _Message.success(message);
      } else {
        _Message.error(message);
      }
      fetchProgress();
    }).finally(() => setSubmitting(false));
  }

  /** 查看默认值：GET preview_url（仅用于读取展示），按后端 columns / data 结构在面板内渲染；再次点击收起 */
  function onViewDefault(step) {
    if (previewStep === step.step_id) {
      setPreviewStep('');
      setPreviewValue(undefined);
      return;
    }
    setPreviewStep(step.step_id);
    setPreviewValue(undefined);
    setPreviewLoading(true);
    request.get(toRequestPath(step.preview_url)).then(res => {
      const {
        code,
        message,
        data
      } = res.data;
      if (code !== 200) {
        _Message.error(message);
        setPreviewStep('');
        return;
      }
      setPreviewValue(data);
    }).catch(() => {
      // 错误提示已由 request 响应拦截器统一处理
      setPreviewStep('');
    }).finally(() => setPreviewLoading(false));
  }

  /**
   * UploadExcel 直传 OSS 成功后回调：POST /init/upload 触发后端异步处理，
   * 随后轮询 /init/step/status 直到 completed 或 failed。
   * 请求体统一为 { file_key, biz_type: 'init', step_id }。
   */
  async function onUploaded(stepId, fileName) {
    // 1) 通知后端拉取 OSS 文件并异步解析入库
    try {
      const res = await request.post('/v1/init/upload', {
        file_key: fileName,
        biz_type: 'init',
        step_id: stepId
      });
      const {
        code,
        message
      } = res.data;
      if (code !== 200) {
        _Message.error(message);
        return;
      }
    } catch {
      // 错误提示已由 request 响应拦截器统一处理
      return;
    }

    // 2) 轮询后台处理状态
    startPolling(stepId);
  }

  /** 启动对某步骤的轮询（幂等：同一 step 已在轮询则跳过） */
  function startPolling(stepId) {
    if (pollingStepRef.current === stepId) return;
    pollCountRef.current = 0;
    pollingStepRef.current = stepId;
    setProcessingStep(stepId);
    pollStepStatus(stepId);
  }

  /** 轮询单步处理状态：completed 完成 / failed 失败 / pending·processing 继续轮询 */
  function pollStepStatus(stepId) {
    request.get('/v1/init/step/status', {
      params: {
        step_id: stepId
      }
    }).then(res => {
      const {
        code,
        data
      } = res.data;
      if (code === 200 && data) {
        if (data.status === STATUS_COMPLETED) {
          stopPoll();
          _Message.success(t['init.hint.process_done']);
          fetchProgress();
          return;
        }
        if (data.status === STATUS_FAILED) {
          stopPoll();
          _Message.error(data.error_msg || t['init.hint.process_failed']);
          fetchProgress();
          return;
        }
      }
      schedulePoll(stepId);
    }).catch(() => schedulePoll(stepId));
  }

  /** 安排下一次轮询；超过上限则停止并提示超时 */
  function schedulePoll(stepId) {
    pollCountRef.current += 1;
    if (pollCountRef.current >= POLL_MAX_TIMES) {
      stopPoll();
      _Message.warning(t['init.hint.process_timeout']);
      fetchProgress();
      return;
    }
    pollTimerRef.current = setTimeout(() => pollStepStatus(stepId), POLL_INTERVAL);
  }

  /** 停止轮询并清除处理中状态 */
  function stopPoll() {
    if (pollTimerRef.current) {
      clearTimeout(pollTimerRef.current);
      pollTimerRef.current = null;
    }
    pollingStepRef.current = '';
    setProcessingStep('');
  }

  /** 下载模板：走 request 携带鉴权头，再以 blob 触发浏览器下载 */
  async function onDownloadTemplate(step) {
    setTemplateLoading(step.step_id);
    try {
      const res = await request.get(toRequestPath(step.template_url), {
        responseType: 'blob'
      });
      const blob = res.data;

      // 后端以 JSON 返回错误时，blob 类型为 application/json
      if (blob.type.includes('application/json')) {
        const text = await blob.text();
        try {
          _Message.error(JSON.parse(text)?.message || text);
        } catch {
          _Message.error(text);
        }
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${step.step_id}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      // 错误提示已由 request 响应拦截器统一处理
    } finally {
      setTemplateLoading('');
    }
  }

  /** 默认收货地址步骤：前端表单提交 */
  async function onAddressSubmit() {
    let values;
    try {
      values = await addressForm.validate();
    } catch {
      return;
    }
    setSubmitting(true);
    request.post('/v1/init/step/address', values).then(res => {
      const {
        code,
        message
      } = res.data;
      if (code === 200) {
        _Message.success(message);
      } else {
        _Message.error(message);
      }
      fetchProgress();
    }).finally(() => setSubmitting(false));
  }

  /** 全部步骤完成后激活企业，成功后整页跳转以重新加载用户信息与初始化数据 */
  function onActivate() {
    setSubmitting(true);
    request.post('/v1/init/activate').then(res => {
      const {
        code,
        message
      } = res.data;
      if (code === 200) {
        _Message.success(message);
        window.location.pathname = '/';
        return;
      }
      _Message.error(message);
    }).finally(() => setSubmitting(false));
  }
  function stepTitle(step) {
    return t[`init.step.${step.step_id}`] || step.title;
  }
  function stepStatus(step) {
    if (isSettled(step)) return 'finish';
    if (step.status === STATUS_FAILED) return 'error';
    if (step.status === STATUS_PROCESSING) return 'process';
    return step.locked ? 'wait' : 'process';
  }
  function stepDesc(step) {
    if (step.status === STATUS_COMPLETED) return t['init.status.done'];
    if (step.status === STATUS_SKIPPED) return t['init.status.skipped'];
    if (step.status === STATUS_FAILED) return t['init.status.failed'];
    if (step.status === STATUS_PROCESSING) return t['init.status.processing'];
    return step.locked ? t['init.status.locked'] : t['init.status.pending'];
  }
  function panelTip(step) {
    if (step.status === STATUS_FAILED) return t['init.hint.failed'];
    if (step.status === STATUS_PROCESSING) return t['init.hint.processing'];
    if (step.step_id === ADDRESS_STEP) return t['init.hint.address'];
    if (canConfirm(step)) return t['init.hint.default'];
    return t['init.hint.upload'];
  }

  /** 当前步骤的操作区：地址表单 / 系统默认值 + 上传 / 仅上传 */
  function renderActions(step) {
    if (step.step_id === ADDRESS_STEP) {
      return /*#__PURE__*/React.createElement(_Form, {
        form: addressForm,
        layout: "vertical",
        className: styles['init-form']
      }, /*#__PURE__*/React.createElement(_Form.Item, {
        label: t['public.label.address_label'],
        field: "label",
        rules: [{
          required: true,
          message: t['public.validate.required']
        }]
      }, /*#__PURE__*/React.createElement(_Input, {
        allowClear: true,
        placeholder: t['public.label.enter'] + t['public.label.address_label']
      })), /*#__PURE__*/React.createElement(_Form.Item, {
        label: t['public.label.consignee'],
        field: "consignee",
        rules: [{
          required: true,
          message: t['public.validate.required']
        }]
      }, /*#__PURE__*/React.createElement(_Input, {
        allowClear: true,
        placeholder: t['public.label.enter'] + t['public.label.consignee']
      })), /*#__PURE__*/React.createElement(_Form.Item, {
        label: t['public.label.consignee_phone'],
        field: "consignee_phone",
        rules: [{
          required: true,
          message: t['public.validate.required']
        }]
      }, /*#__PURE__*/React.createElement(_Input, {
        allowClear: true,
        placeholder: t['public.label.enter'] + t['public.label.consignee_phone']
      })), /*#__PURE__*/React.createElement(_Form.Item, {
        label: t['public.label.ship.address'],
        field: "address",
        rules: [{
          required: true,
          message: t['public.validate.required']
        }]
      }, /*#__PURE__*/React.createElement(_Input.TextArea, {
        allowClear: true,
        autoSize: {
          minRows: 2
        },
        placeholder: t['public.label.enter'] + t['public.label.ship.address']
      })), /*#__PURE__*/React.createElement(_Form.Item, {
        label: t['public.label.default'],
        field: "default",
        initialValue: true
      }, /*#__PURE__*/React.createElement(_Switch, null)), /*#__PURE__*/React.createElement(_Button, {
        type: "primary",
        loading: submitting,
        onClick: onAddressSubmit
      }, t['public.action.submit']));
    }
    const confirmable = canConfirm(step);
    const previewable = canPreview(step);
    return /*#__PURE__*/React.createElement(_Space, {
      wrap: true,
      size: 12
    }, confirmable && /*#__PURE__*/React.createElement(_Button, {
      type: "primary",
      loading: submitting,
      disabled: processingStep === step.step_id,
      onClick: () => onUseDefault(step)
    }, t['init.action.use_default']), previewable && /*#__PURE__*/React.createElement(_Button, {
      icon: /*#__PURE__*/React.createElement(_IconEye, null),
      type: previewStep === step.step_id ? 'secondary' : 'default',
      loading: previewLoading && previewStep === step.step_id,
      onClick: () => onViewDefault(step)
    }, t['init.action.view_default']), /*#__PURE__*/React.createElement(UploadExcel, {
      bizType: OSS_BIZ_TYPE,
      disabled: processingStep === step.step_id,
      buttonText: confirmable || previewable ? t['init.action.upload_custom'] : t['public.action.upload'],
      onSuccess: fileName => onUploaded(step.step_id, fileName)
    }), step.template_url && /*#__PURE__*/React.createElement(_Button, {
      icon: /*#__PURE__*/React.createElement(_IconDownload, null),
      loading: templateLoading === step.step_id,
      onClick: () => onDownloadTemplate(step)
    }, t['public.action.template']), canSkip(step) && /*#__PURE__*/React.createElement(_Popconfirm, {
      title: t['init.action.skip_confirm'],
      onOk: () => onSkip(step)
    }, /*#__PURE__*/React.createElement(_Button, {
      disabled: submitting || processingStep === step.step_id
    }, t['init.action.skip'])));
  }
  function renderPanel() {
    if (allDone) {
      return /*#__PURE__*/React.createElement(_Alert, {
        type: "success",
        showIcon: true,
        content: t['init.hint.all_done']
      });
    }
    if (!current) {
      return /*#__PURE__*/React.createElement(_Empty, {
        description: t['public.hint.no_data']
      });
    }
    if (current.locked) {
      return /*#__PURE__*/React.createElement(_Alert, {
        type: "warning",
        showIcon: true,
        content: t['init.hint.locked']
      });
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: styles['init-panel-title']
    }, `${current.seq}. ${stepTitle(current)}`), current.status === STATUS_FAILED && current.error_msg && /*#__PURE__*/React.createElement(_Alert, {
      type: "error",
      showIcon: true,
      content: current.error_msg,
      className: styles['init-panel-alert']
    }), /*#__PURE__*/React.createElement("div", {
      className: styles['init-panel-tip']
    }, /*#__PURE__*/React.createElement(Text, {
      type: "secondary"
    }, panelTip(current))), renderActions(current), renderProcessing(current), renderDefault(current));
  }

  /** 后台异步处理中提示（轮询期间显示） */
  function renderProcessing(step) {
    if (processingStep !== step.step_id) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: styles['init-panel-preview']
    }, /*#__PURE__*/React.createElement(_Space, null, /*#__PURE__*/React.createElement(_Spin, {
      size: 16
    }), /*#__PURE__*/React.createElement(Text, {
      type: "secondary"
    }, t['init.hint.processing'])));
  }

  /** 默认值预览：统一交由 DefaultPreview 按后端 columns / data 结构渲染 */
  function renderDefault(step) {
    if (previewStep !== step.step_id) return null;
    return /*#__PURE__*/React.createElement("div", {
      className: styles['init-panel-preview']
    }, /*#__PURE__*/React.createElement(DefaultPreview, {
      value: previewValue,
      loading: previewLoading
    }));
  }
  return /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("div", {
    className: styles['init-navbar']
  }, /*#__PURE__*/React.createElement(Navbar, {
    show: true
  })), /*#__PURE__*/React.createElement("div", {
    className: styles['init-content']
  }, /*#__PURE__*/React.createElement(_PageHeader, {
    style: {
      background: 'var(--color-bg-2)',
      marginBottom: 10
    },
    title: t['init.title'],
    subTitle: t['init.subtitle']
  }), /*#__PURE__*/React.createElement(_Card, null, /*#__PURE__*/React.createElement(_Spin, {
    loading: loading,
    style: {
      display: 'block'
    }
  }, steps.length === 0 && !loading ? /*#__PURE__*/React.createElement(_Empty, {
    description: t['public.hint.no_data']
  }) : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Row, {
    gutter: 24
  }, /*#__PURE__*/React.createElement(Col, {
    span: 7
  }, /*#__PURE__*/React.createElement(_Steps, {
    direction: "vertical",
    size: "small",
    current: currentIdx + 1
  }, steps.map(s => /*#__PURE__*/React.createElement(Step, {
    key: s.step_id,
    title: stepTitle(s),
    description: stepDesc(s),
    status: stepStatus(s)
  })))), /*#__PURE__*/React.createElement(Col, {
    span: 17
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['init-panel']
  }, renderPanel()))), /*#__PURE__*/React.createElement("div", {
    className: styles['init-footer']
  }, /*#__PURE__*/React.createElement(Text, {
    type: "secondary"
  }, `${t['init.progress']}: ${doneCount}/${steps.length}`), /*#__PURE__*/React.createElement(_Button, {
    type: "primary",
    disabled: !allDone,
    loading: submitting,
    onClick: onActivate
  }, t['init.action.activate'])))))));
}
InitPage.displayName = 'InitPage';
export default InitPage;